import React, { createContext, useContext, useEffect, useReducer, useState } from 'react';
import { getConfiguration } from '../data/utils/configuration';
import useIsLiveSection from './useIsLiveSection';

const UserSettingsContext = createContext();
const UserSettingsDispatchContext = createContext();

const DBSettings = [
    'betsUnderEvent',
    'compactBetSelector',
    'fastBet',
    'liveSortingType',
    'scrollingBets',
    'scrollingSportSelector',
    'showBanners',
    'showCollapsedCategories',
    'showGoToTopButton',
    'showHighlightedTournaments',
    'showInfoButton',
    'showLiveCategories',
    'showLiveEventsInPrematch',
    'showOnlyFirstBet',
    'showOnlyFirstBetLive',
    'showSocialButtons',
    'sortCategoriesByDesc',
];

window.getSettingsToSaveOnDB = function() {
    if (!localStorage.hasOwnProperty('xs_user_settings')) {
        return null;
    }

    let settings = null;
    try {
        settings = localStorage.getItem('xs_user_settings');
        settings = JSON.parse(settings);

        const settingsToSave = {};
        DBSettings.forEach(key => {
            if (settings[key] != null) {
                settingsToSave.key = settings[key];
            }
        });
        if (Object.keys(settingsToSave).length === 0) {
            settings = null;
        }
    } catch (e) {
        console.error(e);
    }

    return settings;
};

export function useUserSettings() {
    return useContext(UserSettingsContext);
}

export function useUserSettingsDispatch() {
    return useContext(UserSettingsDispatchContext);
}

export function useShowOnlyFirstBet() {
    const userSettings = useUserSettings();
    const isLiveSection = useIsLiveSection();

    if (isLiveSection) {
        return userSettings.showOnlyFirstBetLive;
    } else {
        return userSettings.showOnlyFirstBet;
    }
}

function loadUserSettings() {
    const defaultSettings = getDefaultUserSettings();

    if (window.settingsFromDB) {
        const settings = {
            ...defaultSettings,
            ...window.settingsFromDB
        };
        window.settingsFromDB = null;
        return settings;
    } else if (localStorage.hasOwnProperty('xs_user_settings')) {
        const savedSettings = JSON.parse(localStorage.getItem('xs_user_settings'));
        return {
            ...defaultSettings,
            ...savedSettings
        };
    } else {
        // Save default settings on local storage so that they are saved on the DB on landing (if needed)
        const settingsToSave = {};
        DBSettings.forEach(key => {
            if (defaultSettings[key] != null) {
                settingsToSave[key] = defaultSettings[key];
            }
        });
        saveUserSettings(settingsToSave);
        return settingsToSave;
    }
}

function saveUserSettings(settings) {
    localStorage.setItem('xs_user_settings', JSON.stringify(settings));
    return settings;
}

function userSettingsReducer(flags, action) {
    const type = action.type ? action.type : action;
    switch (type) {
        case 'toggleDarkTheme': {
            return saveUserSettings({
                ...flags,
                darkTheme: !flags.darkTheme 
            });
        }
        case 'toggleScrollingSportSelector': {
            return saveUserSettings({
                ...flags,
                scrollingSportSelector: !flags.scrollingSportSelector 
            });
        }
        case 'toggleScrollingBets': {
            const scrollingBets = !flags.scrollingBets;
            return saveUserSettings({
                ...flags,
                scrollingBets,
                showOnlyFirstBet: scrollingBets ? false : flags.showOnlyFirstBet
            });
        }
        case 'toggleShowOnlyFirstBet': {
            const showOnlyFirstBet = !flags.showOnlyFirstBet;
            return saveUserSettings({
                ...flags,
                showOnlyFirstBet,
                scrollingBets: showOnlyFirstBet ? false : flags.scrollingBets
            });
        }
        case 'toggleShowOnlyFirstBetLive': {
            const showOnlyFirstBetLive = !flags.showOnlyFirstBetLive;
            return saveUserSettings({
                ...flags,
                showOnlyFirstBetLive
            });
        }
        case 'toggleSortCategoriesByDesc': {
            return saveUserSettings({
                ...flags,
                sortCategoriesByDesc: !flags.sortCategoriesByDesc
            });
        }
        case 'toggleCompactBetSelector': {
            const compactBetSelector = !flags.compactBetSelector;
            return saveUserSettings({
                ...flags,
                compactBetSelector: compactBetSelector,
                showOnlyFirstBet: compactBetSelector ? false : flags.showOnlyFirstBet
            });
        }
        case 'toggleBetsUnderEvent': {
            return saveUserSettings({
                ...flags,
                betsUnderEvent: !flags.betsUnderEvent
            });
        }
        case 'toggleShowHighlightedTournaments': {
            return saveUserSettings({
                ...flags,
                showHighlightedTournaments: !flags.showHighlightedTournaments
            });
        }
        case 'toggleshowLiveEventsInPrematch': {
            return saveUserSettings({
                ...flags,
                showLiveEventsInPrematch: !flags.showLiveEventsInPrematch
            });
        }
        case 'toggleShowBonusAdvice': {
            return saveUserSettings({
                ...flags,
                showBonusAdvice: !flags.showBonusAdvice
            });
        }
        case 'setLiveSortingType': {
            return saveUserSettings({
                ...flags,
                liveSortingType: action.liveSortingType
            });
        }
        case 'toggleShowSocialButtons': {
            return saveUserSettings({
                ...flags,
                showSocialButtons: !flags.showSocialButtons
            });
        }
        case 'toggleShowLiveCategories': {
            return saveUserSettings({
                ...flags,
                showLiveCategories: !flags.showLiveCategories
            });
        }
        case 'toggleShowCollapsedCategories': {
            return saveUserSettings({
                ...flags,
                showCollapsedCategories: !flags.showCollapsedCategories
            });
        }
        case 'toggleShowBanners': {
            return saveUserSettings({
                ...flags,
                showBanners: !flags.showBanners
            });
        }
        case 'toggleGoToTopButton': {
            return saveUserSettings({
                ...flags,
                showGoToTopButton: !flags.showGoToTopButton
            });
        }
        case 'toggleInfoButton': {
            return saveUserSettings({
                ...flags,
                showInfoButton: !flags.showInfoButton
            });
        }
        default: {
            throw Error('Unknown action: ' + action);
        }
    }
}

export function getDefaultUserSettings() {
    const conf = getConfiguration();
    const defaultConf = conf.ms;
    return {
        betsUnderEvent: defaultConf?.betsUnderEvent ?? false,
        compactBetSelector: defaultConf?.compactBetSelector ?? true,
        darkTheme: defaultConf?.darkTheme ?? false,
        fastBet: defaultConf?.fastBet ?? false,
        liveSortingType: defaultConf?.liveSortingType ?? 'league',
        scrollingBets: defaultConf?.scrollingBets ?? false,
        scrollingSportSelector: defaultConf?.scrollingSportSelector ?? true,
        showBanners: defaultConf?.showBanners ?? true,
        showBonusAdvice: defaultConf?.showBonusAdvice ?? true,
        showCollapsedCategories: defaultConf?.showCollapsedCategories ?? false,
        showGoToTopButton: defaultConf?.showGoToTopButton ?? true,
        showHighlightedTournaments: defaultConf?.showHighlightedTournaments ?? true,
        showInfoButton: defaultConf?.showInfoButton ?? false,
        showLiveCategories: defaultConf?.showLiveCategories ?? true,
        showLiveEventsInPrematch: defaultConf?.showLiveEventsInPrematch ?? true,
        showOnlyFirstBet: defaultConf?.showOnlyFirstBet ?? false,
        showOnlyFirstBetLive: defaultConf?.showOnlyFirstBetLive ?? true,
        showSocialButtons: defaultConf?.showSocialButtons ?? true,
        sortCategoriesByDesc: defaultConf?.sortCategoriesByDesc ?? false,
    };
};

export function UserSettingsProviderInner(props) {
    const [flags, dispatch] = useReducer(userSettingsReducer, loadUserSettings());

    return (
        <UserSettingsContext.Provider value={flags}>
            <UserSettingsDispatchContext.Provider value={dispatch}>
                {props.children}
            </UserSettingsDispatchContext.Provider>
        </UserSettingsContext.Provider>
    );
}

export default function UserSettingsProvider(props) {
    const [key, setKey] = useState(null);

    useEffect(() => {
        const updateUserSettings = () => setKey(+ new Date());

        window.addEventListener('xs_updateUserSettings', updateUserSettings);
        return () => window.removeEventListener('xs_updateUserSettings', updateUserSettings);
    }, []);

    return (
        <UserSettingsProviderInner key={key}>
            {props.children}
        </UserSettingsProviderInner>
    );
}
